import { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

interface CourseCardProps {
  title: string;
  tagline?: string;
  description: string;
  image: string;
  video?: string;
  instructor: {
    name: string;
    image: string;
  };
  gradient?: string;
  badge?: string;
  ctaText?: string;
  onCtaClick?: () => void;
  isLoading?: boolean;
}

const CourseCard = ({
  title,
  tagline,
  description,
  image,
  video,
  instructor,
  gradient = "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  badge,
  ctaText = "Learn More →",
  onCtaClick,
  isLoading = false,
}: CourseCardProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Auto-play video on hover, pause when not hovering
  useEffect(() => {
    if (videoRef.current) {
      if (isHovered) {
        videoRef.current.play().catch(() => {
          // Video play failed
        });
      } else {
        videoRef.current.pause();
        videoRef.current.currentTime = 0; // Reset to start
      }
    }
  }, [isHovered]);

  return (
    <motion.div
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      whileHover={{ y: -8 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      className="h-full"
    >
      <Card className="relative h-full overflow-hidden bg-card/50 backdrop-blur-sm border-white/10 hover:border-white/20 transition-all duration-300 flex flex-col">
        {/* Image/Video Section */}
        <div className="relative h-64 overflow-hidden group">
          {/* Static Image - Always visible */}
          {/* NOTE: Image should be optimized to ~800x500px (2x retina: 1600x1000px) for this h-64 display */}
          <motion.img
            src={image}
            alt={title}
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
            width={800}
            height={500}
            animate={{
              scale: isHovered && !video ? 1.05 : 1,
              opacity: isHovered && video ? 0 : 1,
            }}
            transition={{ duration: 0.5 }}
          />

          {/* Video overlay - plays on hover */}
          {video && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: isHovered ? 1 : 0 }}
              transition={{ duration: 0.3 }}
              className="absolute inset-0 z-10"
            >
              <video
                ref={videoRef}
                src={video}
                loop
                muted
                playsInline
                className="w-full h-full object-cover"
              />
            </motion.div>
          )}

          {/* Gradient Overlay */}
          <div
            className="absolute inset-0 opacity-40 mix-blend-multiply z-20"
            style={{ background: gradient }}
          />

          {/* Badge - shows course type (e.g., "Invite Only") */}
          {badge && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
              className="absolute top-4 left-4 z-30 backdrop-blur-sm rounded-full px-4 py-1.5"
              style={{
                background: "linear-gradient(135deg, #D4AF37 0%, #F4E4C1 50%, #C9A961 100%)",
                boxShadow: "0 0 25px rgba(212, 175, 55, 0.5), 0 0 50px rgba(212, 175, 55, 0.25), 0 4px 15px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.3)",
                border: "1px solid rgba(244, 228, 193, 0.4)",
              }}
            >
              <span 
                className="text-xs font-bold uppercase tracking-wide"
                style={{
                  background: "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                  textShadow: "0 1px 2px rgba(0, 0, 0, 0.1)",
                }}
              >
                {badge}
              </span>
            </motion.div>
          )}

          {/* Video Indicator Badge - shows "Playing..." when hovering */}
          {video && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ 
                opacity: isHovered ? 1 : 0,
                scale: isHovered ? 1 : 0.9,
              }}
              transition={{ duration: 0.3 }}
              className="absolute top-4 right-4 z-30 bg-black/60 backdrop-blur-sm rounded-full px-3 py-1"
            >
              <span className="text-white text-xs font-medium flex items-center gap-1">
                <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                Playing
              </span>
            </motion.div>
          )}

          {/* Instructor Badge */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="absolute bottom-4 left-4 z-30 flex items-center gap-2 bg-black/60 backdrop-blur-sm rounded-full px-3 py-2"
          >
            <img
              src={instructor.image}
              alt={instructor.name}
              className="w-8 h-8 rounded-full object-cover border-2 border-white/20"
            />
            <span className="text-white text-sm font-medium">{instructor.name}</span>
          </motion.div>
        </div>

        {/* Content Section */}
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-foreground">
            {title}
          </CardTitle>
          {tagline && (
            <CardDescription className="text-base text-muted-foreground font-medium">
              {tagline}
            </CardDescription>
          )}
        </CardHeader>

        <CardContent className="flex-grow">
          <p className="text-muted-foreground leading-relaxed">
            {description}
          </p>
        </CardContent>

        <CardFooter className="mt-auto">
          <Button
            onClick={onCtaClick}
            disabled={isLoading}
            className={`w-full font-semibold transition-all duration-300 flex items-center justify-center gap-2 ${!badge ? 'bg-primary hover:bg-primary/90 text-primary-foreground' : ''}`}
            size="lg"
            style={badge ? {
              background: isLoading 
                ? "rgba(212, 175, 55, 0.5)" 
                : "linear-gradient(135deg, #D4AF37 0%, #F4E4C1 50%, #C9A961 100%)",
              color: "#1a1a1a",
              border: "1px solid rgba(244, 228, 193, 0.4)",
              boxShadow: isLoading 
                ? "none"
                : "0 0 20px rgba(212, 175, 55, 0.4), 0 4px 12px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.2)",
              cursor: isLoading ? "not-allowed" : "pointer",
            } : {
              cursor: isLoading ? "not-allowed" : "pointer",
              opacity: isLoading ? 0.7 : 1,
            }}
            onMouseEnter={(e) => {
              if (badge && !isLoading) {
                e.currentTarget.style.background = "linear-gradient(135deg, #E5C158 0%, #F9F0D9 50%, #D4AF37 100%)";
                e.currentTarget.style.boxShadow = "0 0 30px rgba(212, 175, 55, 0.6), 0 6px 16px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.3)";
              }
            }}
            onMouseLeave={(e) => {
              if (badge && !isLoading) {
                e.currentTarget.style.background = "linear-gradient(135deg, #D4AF37 0%, #F4E4C1 50%, #C9A961 100%)";
                e.currentTarget.style.boxShadow = "0 0 20px rgba(212, 175, 55, 0.4), 0 4px 12px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.2)";
              }
            }}
          >
            {isLoading ? (
              <>
                <Loader2 className="animate-spin" size={20} />
                Processing...
              </>
            ) : (
              ctaText
            )}
          </Button>
        </CardFooter>

        {/* Hover glow effect */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: isHovered ? 1 : 0 }}
          className="absolute inset-0 pointer-events-none"
          style={{
            background: `radial-gradient(circle at center, ${gradient.match(/\#[a-fA-F0-9]{6}/)?.[0] || '#667eea'}20, transparent 70%)`,
          }}
        />
      </Card>
    </motion.div>
  );
};

export default CourseCard;

